<template>
  <div id="LGA">
    <el-card
      shadow="hover"
      class="mainCard clear-card"
      :body-style="{
        padding: '20px 30px',
      }"
    >
      <div class="title defaultCursor">
        {{ $t('common.lgaLong') }}
      </div>

      <div class="description defaultCursor">
        {{ $t('lga.desc') }}:
      </div>

      <el-form
        ref="LGAForm"
        :model="form"
        :rules="rules"
        class="lgaForm"
        label-position="right"
        label-width="20px"
        :style="{padding: cardPadding}"
      >
        <el-form-item
          label="1."
          prop="lga1"
        >
          <el-input
            v-model="form.lga1"
            :placeholder="$t('lga.typeLga')"
            :maxlength="255"
            :class="{published: lgas.status === 'published'}"
            :disabled="lgas.status === 'published'"
            @change="formatInput"
          >
            <div
              slot="suffix"
              class="suffix"
            >
              {{ lga1WordCount }}/{{ maxWordCount }}
            </div>
          </el-input>
        </el-form-item>
        <el-form-item
          label="2."
          prop="lga2"
        >
          <el-input
            v-model="form.lga2"
            :placeholder="$t('lga.typeLga')"
            :class="{published: lgas.status === 'published'}"
            :disabled="(form.lga1 === '' && form.lga2 === '') || lgas.status === 'published'"
            :maxlength="255"
            @change="formatInput"
          >
            <div
              slot="suffix"
              class="suffix"
            >
              {{ lga2WordCount }}/{{ maxWordCount }}
            </div>
          </el-input>
        </el-form-item>
      </el-form>
    </el-card>

    <div
      v-if="lgas.status !== 'published'"
      class="actions mainCard"
    >
      <el-button
        type="primary"
        round
        @click="saveLGAs"
      >
        {{ $t('common.save') }}
      </el-button>
      <el-button
        :disabled="!canPublish"
        round
        @click="publishLGAs"
      >
        {{ $t('common.publish') }}
      </el-button>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';

export default {
  data() {
    return {
      form: {
        lga1: '',
        lga2: '',
      },
      rules: {
        lga1: [
          { validator: this.checkLength, trigger: 'change' },
        ],
        lga2: [
          { validator: this.checkLength, trigger: 'change' },
        ],
      },
      maxWordCount: 10,
      validation: false,
    };
  },

  computed: {
    ...mapState({
      lgas: (state) => state.myLGAs.lgas,
      session: (state) => state.processStatus.process.session || 0,
    }),

    lga1WordCount() {
      if (this.form.lga1 && this.form.lga1 !== '' && this.form.lga1.length) {
        return (this.form.lga1.trim()).split(' ').length;
      }
      return 0;
    },

    lga2WordCount() {
      if (this.form.lga2 && this.form.lga2 !== '' && this.form.lga2.length) {
        return (this.form.lga2.trim()).split(' ').length;
      }
      return 0;
    },

    canPublish() {
      if (this.session < 2) return false;
      if (process.env.VUE_APP_MIN_LGA * 1 === 1) {
        if (this.form.lga1.length) return true;
      }
      if (process.env.VUE_APP_MIN_LGA * 1 === 2) {
        if (this.form.lga1.length && this.form.lga2.length) return true;
      }
      return false;
    },

    cardPadding() {
      let padding = '0 20%';
      if (this.$mq === 'mobile') {
        padding = '0';
      }
      return padding;
    },
  },

  watch: {
    lgas: {
      handler(newValue) {
        this.form.lga1 = newValue.lga1 ? newValue.lga1 : '';
        this.form.lga2 = newValue.lga2 ? newValue.lga2 : '';
      },
      deep: true,
    },
  },

  created() {
    this.init();
  },

  methods: {
    ...mapActions('myLGAs', [
      'updateLGAs',
    ]),

    ...mapActions('processStatus', [
      'fetchProcess',
    ]),

    checkLength(rule, value, callback) {
      let wordCount = 0;
      if (value && value.length > 0 && value !== '') {
        wordCount = value.trim().split(' ').length;
      }

      if (wordCount > this.maxWordCount) {
        callback(new Error(this.$t('lga.notMore10')));
      } else {
        callback();
      }
    },

    init() {
      this.form.lga1 = this.lgas && this.lgas.lga1 ? this.lgas.lga1 : '';
      this.form.lga2 = this.lgas && this.lgas.lga2 ? this.lgas.lga2 : '';
    },

    formatInput() {
      if (this.form.lga1) {
        this.form.lga1 = this.form.lga1.charAt(0).toUpperCase() + this.form.lga1.slice(1);
      }

      if (this.form.lga2) {
        this.form.lga2 = this.form.lga2.charAt(0).toUpperCase() + this.form.lga2.slice(1);
      }
    },

    async saveLGAs() {
      try {
        this.$refs.LGAForm.validate(async (vaild) => {
          if (vaild) {
            await this.updateLGAs(this.form);
            this.$message.success(this.$t('lga.lgaSaved'));
            // this.$alert('Your Leadership Growth Areas have been saved.', '', {
            //   confirmButtonText: 'Ok',
            //   type: 'success',
            // });
            this.fetchProcess();
          } else {
            this.$message.error(this.$t('lga.notMore10'));
          }
        });
      } catch (err) {
        console.log('error submit', err);

        if (err.msg) {
          this.$message.error(err.msg);
        }
      }
    },

    publishLGAs() {
      try {
        this.$confirm(
          this.$t('lga.lgaConfirm'),
          '', {
            confirmButtonText: this.$t('common.yes'),
            cancelButtonText: this.$t('common.no'),
          },
        ).then(async () => {
          try {
            this.$refs.LGAForm.validate(async (valid) => {
              if (valid) {
                const data = { ...this.form, status: 'published' };
                await this.updateLGAs(data);
                this.$message.success(this.$t('lga.lgaPublished'));
                // this.$alert('Your Leadership Growth Areas has been published', '', {
                //   confirmButtonText: 'Ok',
                //   type: 'success',
                // });
                this.fetchProcess();
              } else {
                this.$message.error(this.$t('lga.notMore10'));
              }
            });
          } catch (err) {
            if (err.msg) {
              this.$message.error(err.msg);
            }
          }
        }).catch(() => {});
      } catch (err) {
        console.log('error submit', err);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#LGA {
  .el-card {
    margin-bottom: 20px;

    .title {
      margin-bottom: 10px;
      font-weight: bold;
    }

    .description {
      margin-bottom: 20px;
    }

    .lgaForm {
      padding: 0 20%;
    }
  }

  .actions {
    text-align: center;

    .el-button {
      width: 100px;
      padding: 13px 0;
    }
  }
}
</style>

<style lang="scss">
  #LGA {
    .lgaForm {
      .el-form-item__label {
        line-height: 40px;
      }

      .el-form-item__content {
        .el-input__inner {
          padding-left: 0;
          height: 25px;
          line-height: 22px;
        }

        .published {
          .el-input__inner {
            cursor: default;
          }
        }
      }
    }
  }
</style>
